import { Injectable } from '@angular/core';
import { User, UserRole } from '../models/user.model';

/**
 * Canonical labels for the signed-in user everywhere in PPMS (header, sidebar, home).
 * Minister name + designation are fixed per product policy; all other fields come from Firestore.
 */
@Injectable({ providedIn: 'root' })
export class UserProfileDisplayService {
  readonly ministerPublicName = 'Sri Suryabanshi Suraj';
  readonly ministerDesignation = 'Hon\'ble Minister';

  displayName(user: User | null | undefined): string {
    if (!user) {
      return '';
    }
    if (user.role === UserRole.MINISTER) {
      return this.ministerPublicName;
    }
    const fromDoc = (user.fullName ?? user.displayName ?? '').trim();
    if (fromDoc) {
      return fromDoc;
    }
    return (user.email?.split('@')[0] ?? 'User').trim() || 'User';
  }

  /**
   * Uses Firestore `designation` when set; otherwise the default title for the user's role.
   */
  designation(user: User | null | undefined): string {
    if (!user) {
      return '';
    }
    if (user.role === UserRole.MINISTER) {
      return this.ministerDesignation;
    }
    const custom = (user.designation ?? '').trim();
    if (custom) {
      return custom;
    }
    switch (user.role) {
      case UserRole.ADMIN:
        return 'Administrator';
      case UserRole.OSD:
        return 'Officer on Special Duty';
      case UserRole.APPROVER:
        return 'Department Approver';
      case UserRole.OPERATOR:
        return 'Department Operator';
      default:
        return '';
    }
  }

  /** Uppercase role keyword for badges (MINISTER, OSD, …). */
  roleBadge(user: User | null | undefined): string {
    if (!user?.role) {
      return '';
    }
    return String(user.role).toUpperCase();
  }

  /**
   * Absolute or relative image URL when the user document provides one;
   * otherwise the default minister portrait asset.
   */
  avatarUrl(_user: User | null | undefined): string | null {
    return null;
  }

  avatarInitial(user: User | null | undefined): string {
    const n = this.displayName(user);
    return n ? n.charAt(0).toUpperCase() : 'U';
  }
}
